import { useEffect, useRef, useState } from 'react';
import style from './Tasks.module.css';
import ChecklistSection from './ChecklistSection';
import ReactQuill from 'react-quill';
import { Button } from 'react-bootstrap';
import plusIcon from 'assets/img/plusIcon.svg';
import expandIcon from 'assets/img/expandIcon.svg';
import minimizeIcon from 'assets/img/minimizeIcon.svg';
import { useSocket } from '../../context/SocketContext';
import { buildCrossSectionPayload, buildReorderPayload, getPlainText } from 'utils/Helpers';
import { DragDropContext } from '@hello-pangea/dnd';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import Tippy from '@tippyjs/react';
import backIcon4 from 'assets/img/back-icon4.svg';
export default function ChecklistContainer({
  sections,
  showSubTask,
  subtaskId = '',
  projectBoardId,
  milestoneId,
  isChecklistFullscreen,
  setIsChecklistFullscreen,
  viewMode,
  ...taskProps
}) {
  const bottomRef = useRef(null);
  const socket = useSocket();
  const [openSectionDropdownId, setOpenSectionDropdownId] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [checklistName, setChecklistName] = useState('');
  const quillRef = useRef(null);

  useEffect(() => {
    if (showInput && quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.focus();
    }
  }, [showInput]);

  const handleSaveChecklist = () => {
    const plainText = checklistName.trim();

    if (!plainText || plainText.length < 2) return;

    if (showSubTask) {
      socket.emit(
        'add-checklist',
        {
          name: plainText,
          subtaskId,
          parentChecklistId: null
        },
        projectBoardId
      );
    } else {
      socket.emit(
        'add-checklist',
        {
          name: plainText,
          milestoneId,
          parentChecklistId: null
        },
        projectBoardId
      );
    }

    setChecklistName('');
    setShowInput(false);
  };

  const handleMoveSection = (sectionId, direction) => {
    const sorted = [...sortedSections];

    const index = sorted.findIndex((s) => s._id === sectionId);
    if (index === -1) return;

    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sorted.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap positions in array
    [sorted[index], sorted[targetIndex]] = [sorted[targetIndex], sorted[index]];

    // Now assign NEW order values based on new positions
    sorted.forEach((section, idx) => {
      if (showSubTask) {
        socket.emit(
          'update-checklist',
          {
            id: section._id,
            subtaskId,
            payload: { order: idx }
          },
          projectBoardId
        );
      } else {
        socket.emit(
          'update-checklist',
          {
            id: section._id,
            milestoneId,
            payload: { order: idx }
          },
          projectBoardId
        );
      }
    });
  };

  const handleGlobalDragEnd = (result) => {
    const { source, destination, draggableId, type } = result;
    if (source?.droppableId === destination?.droppableId && source?.index === destination?.index) {
      return;
    }
    if (!destination) return;

    // ✅ SECTION DRAG
    if (type === 'CHECKLIST_SECTION') {
      const items = Array.from(sortedSections);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);

      items.forEach((section, idx) => {
        if (showSubTask) {
          socket.emit(
            'update-checklist',
            {
              id: section._id,
              subtaskId,
              payload: { order: idx }
            },
            projectBoardId
          );
        } else {
          socket.emit(
            'update-checklist',
            {
              id: section._id,
              milestoneId,
              payload: { order: idx }
            },
            projectBoardId
          );
        }
      });

      return;
    }

    const sourceSectionId = source.droppableId;
    const destSectionId = destination.droppableId;

    // ✅ SAME SECTION → just reorder
    if (sourceSectionId === destSectionId) {
      if (showSubTask) {
        socket.emit(
          'update-checklist-orders',
          {
            subtaskId,
            items: buildReorderPayload(sourceSectionId, source.index, destination.index, sections)
          },
          projectBoardId
        );
      } else {
        socket.emit(
          'update-checklist-orders',
          {
            milestoneId,
            items: buildReorderPayload(sourceSectionId, source.index, destination.index, sections)
          },
          projectBoardId
        );
      }
      return;
    }

    // ✅ DIFFERENT SECTION → move + reorder
    if (showSubTask) {
      socket.emit(
        'update-checklist',
        {
          id: draggableId,
          subtaskId,
          payload: {
            parentChecklistId: destSectionId
          }
        },
        projectBoardId
      );
    } else {
      // 1️⃣ update parent
      socket.emit(
        'update-checklist',
        {
          id: draggableId,
          milestoneId,
          payload: {
            parentChecklistId: destSectionId
          }
        },
        projectBoardId
      );
    }

    // 2️⃣ reorder destination list
    if (showSubTask) {
      socket.emit(
        'update-checklist-orders',
        {
          subtaskId,
          skipActivityOnParentChange: true,
          items: buildCrossSectionPayload(draggableId, sourceSectionId, destSectionId, destination.index, sections)
        },
        projectBoardId
      );
    } else {
      socket.emit(
        'update-checklist-orders',
        {
          milestoneId,
          skipActivityOnParentChange: true,
          items: buildCrossSectionPayload(draggableId, sourceSectionId, destSectionId, destination.index, sections)
        },
        projectBoardId
      );
    }
  };

  const sortedSections = [...(sections || [])]
    .map((section, index) => ({
      ...section,
      __index: index
    }))
    .sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : Infinity;
      const orderB = typeof b.order === 'number' ? b.order : Infinity;

      if (orderA === orderB) {
        return a.__index - b.__index; // stable fallback
      }

      return orderA - orderB;
    });

  const handleAddChecklistClick = () => {
    setShowInput(true);

    // wait for input to render, then scroll
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }, 50);
  };

  return (
    <div className={style.checklistContainer}>
      {isChecklistFullscreen && (
        <div className={style.fullscreen_back}>
          <button onClick={() => setIsChecklistFullscreen(false)} className="fullscreen-btn">
            <img src={backIcon4} alt="backIcon4 icon" className="backIcon4-img" />
            Back to task
          </button>
        </div>
      )}
      <div className={style.container_icons}>
        {sortedSections.length > 0 && !viewMode && (
          <Tippy content={`${isChecklistFullscreen ? 'Exit' : ''} Full Screen`} placement="top" arrow={true}>
            <img
              className={style.expand_checklist_icon}
              src={isChecklistFullscreen ? minimizeIcon : expandIcon}
              alt="menu"
              onClick={() => setIsChecklistFullscreen(!isChecklistFullscreen)}
            />
          </Tippy>
        )}
        {!viewMode && (
          <Tippy content={'Add Checklist'} placement="top" arrow={true}>
            <img className={style.add_checklist_icon} src={plusIcon} alt="menu" onClick={handleAddChecklistClick} />
          </Tippy>
        )}
      </div>
      <DragDropContext onDragEnd={handleGlobalDragEnd}>
        <Droppable droppableId="CHECKLIST_SECTIONS" type="CHECKLIST_SECTION">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {sortedSections?.map((section, index) => (
                <Draggable key={section._id} draggableId={section._id} index={index}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <ChecklistSection
                        dragHandleProps={provided.dragHandleProps}
                        tasks={section.children || []}
                        section={section}
                        index={index}
                        showSubTask={showSubTask}
                        subtaskId={subtaskId}
                        projectBoardId={projectBoardId}
                        milestoneId={milestoneId}
                        dropdownOpen={openSectionDropdownId === section._id}
                        onDropdownToggle={(isOpen) => setOpenSectionDropdownId(isOpen ? section._id : null)}
                        viewMode={viewMode}
                        {...taskProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {!showInput && viewMode === false && (
        <button
          className={`${style.add_item_button} ${style.pad10}`}
          style={{ width: 'fit-content' }}
          onClick={handleAddChecklistClick}>
          <div className="icon-btn">
            <img src={plusIcon} alt="search-icon" className="search-icon-right" />
          </div>{' '}
          Add Checklist
        </button>
      )}

      {showInput && viewMode === false && (
        <div className="w-100 mt-2">
          <input
            type="text"
            className={style.rename_input}
            value={getPlainText(checklistName)}
            autoFocus
            placeholder="Add checklist name"
            onChange={(e) => setChecklistName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveChecklist();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowInput(false);
              }
            }}
          />

          <div className="mt-2 d-flex gap-2">
            <Button variant="success" onClick={handleSaveChecklist}>
              Save
            </Button>
            <Button variant="link" onClick={() => setShowInput(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
